import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/pricing";
import { itemRows } from "@/lib/orderDetails";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Trash2, CakeSlice } from "lucide-react";
import { toast } from "sonner";

const ItemView = ({ it, i, catalog }) => {
  const rows = itemRows(catalog, it);
  const photos = rows.filter((r) => r.image);
  return (
    <div data-testid={`admin-item-${i}`} className="border-b border-sugar-border py-4 last:border-0">
      <div className="flex gap-3">
        {it.image ? <img src={it.image} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" /> : <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-sugar-blush text-sugar-pink"><CakeSlice size={20} /></span>}
        <div className="min-w-0 flex-1">
          <div className="flex justify-between gap-2"><span className="font-bold text-sugar-ink">{it.style_name}</span><span className="font-bold text-sugar-pink">{money(it.unit_price)}</span></div>
          <ul className="mt-1.5 space-y-0.5">
            {rows.filter((r) => !r.image).map((r, j) => (
              <li key={j} className="text-xs text-sugar-ink2"><span className="text-sugar-muted">{r.label}:</span> <b className="text-sugar-ink">{r.value}</b></li>
            ))}
          </ul>
        </div>
      </div>
      {photos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {photos.map((p, j) => (
            <a key={j} href={p.image} target="_blank" rel="noreferrer" title={p.label} data-testid={`admin-item-${i}-photo-${j}`}>
              <img src={p.image} alt={p.label} className="h-20 w-20 rounded-lg border border-sugar-border object-cover transition-transform hover:scale-105" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export const OrderItemsEditor = ({ order, catalog, onSaved }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const update = (i, k, v) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setSaving(true);
    try {
      const items = draft.map((x) => ({ ...x, unit_price: Number(x.unit_price) || 0 }));
      const { data } = await api.patch(`/admin/orders/${order.id}`, { items });
      onSaved(data); setDraft(null); toast.success("Items updated — totals recalculated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-sugar-border bg-white p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-sugar-ink">Items</h3>
        {!draft && <button data-testid="admin-edit-items" onClick={() => setDraft(order.items.map((x) => ({ ...x })))} className="inline-flex items-center gap-1 text-xs font-bold text-sugar-pink hover:underline"><Pencil size={13} /> Edit items</button>}
      </div>
      {!draft && order.items.map((it, i) => <ItemView key={i} it={it} i={i} catalog={catalog} />)}
      {draft && (
        <div className="mt-3 space-y-3">
          {draft.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input data-testid={`admin-item-name-${i}`} value={it.style_name} onChange={(e) => update(i, "style_name", e.target.value)} className="flex-1 rounded-xl border-sugar-border" />
              <Input data-testid={`admin-item-price-${i}`} type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} className="w-28 rounded-xl border-sugar-border" />
              <button data-testid={`admin-item-remove-${i}`} disabled={draft.length === 1} onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} className="text-sugar-muted hover:text-red-500 disabled:opacity-30"><Trash2 size={17} /></button>
            </div>
          ))}
          <p className="text-xs text-sugar-muted">Changing prices recalculates tax and total. If the customer now owes more, send them a pay link; if they paid too much, issue a refund.</p>
          <div className="flex gap-2">
            <Button data-testid="admin-save-items" disabled={saving} onClick={save} className="rounded-full bg-sugar-pink font-bold text-white hover:bg-sugar-pinkhover">{saving ? "Saving…" : "Save items"}</Button>
            <Button data-testid="admin-cancel-items" variant="ghost" onClick={() => setDraft(null)} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
